import { useEffect, useId, useRef, useState } from "react";
import {
  ChevronsUpDown, LogOut, Moon, Sun, User, KeyRound, LifeBuoy,
} from "lucide-react";
import { t } from "./i18n.js";
import { useEu } from "./Sessao.js";
import { alternarTema, temaAtual, type Tema } from "../lib/tema.js";

export function iniciaisDe(nome: string | null): string {
  const partes = (nome ?? "").trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  // Primeiro e ÚLTIMO nome. Pegar os dois primeiros transforma
  // "Fernando Guilherme Alves Moreira" em FG, que não identifica ninguém.
  const primeiro = partes[0]![0]!;
  const ultimo = partes.length > 1 ? partes[partes.length - 1]![0]! : "";
  return (primeiro + ultimo).toUpperCase();
}

/**
 * Menu do usuário: quem sou, em qual empresa estou, e como saio.
 *
 * Sair é REQUISITO, não enfeite. Sistema que guarda CPF e salário sem botão de
 * sair obriga a fechar o navegador, e em máquina compartilhada de escritório
 * isso é a sessão aberta esperando a próxima pessoa sentar.
 */
export function MenuUsuario({ recolhido }: { recolhido: boolean }) {
  const eu = useEu();
  const [aberto, setAberto] = useState(false);
  const [tema, setTema] = useState<Tema>("claro");
  const [saindo, setSaindo] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  const gatilho = useRef<HTMLButtonElement>(null);
  const id = useId();

  useEffect(() => { setTema(temaAtual()); }, []);

  useEffect(() => {
    if (!aberto) return;
    const foraDaCaixa = (e: MouseEvent) =>
      caixa.current && !caixa.current.contains(e.target as Node) && setAberto(false);
    const tecla = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setAberto(false);
      // Devolver o foco ao gatilho: sem isso, quem navega por teclado fecha o
      // menu e o foco volta para o início da página.
      gatilho.current?.focus();
    };
    document.addEventListener("mousedown", foraDaCaixa);
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("mousedown", foraDaCaixa);
      document.removeEventListener("keydown", tecla);
    };
  }, [aberto]);

  const sair = async () => {
    setSaindo(true);
    // O logout é do SERVIDOR: limpar só o estado local deixaria o cookie de
    // sessão vivo, e voltar para a URL entraria de novo.
    try { await fetch("/auth/logout", { method: "POST" }); } catch { /* sai assim mesmo */ }
    localStorage.removeItem("eop:empresa");
    window.location.assign("/");
  };

  const trocarEmpresa = (companyId: string) => {
    if (companyId === eu.empresaAtual) { setAberto(false); return; }
    localStorage.setItem("eop:empresa", companyId);
    // Recarrega em vez de refazer o estado: trocar de empresa invalida TODO o
    // cache em memória, e sobrar uma tela com dado da empresa anterior é o
    // vazamento que a RLS impede no banco e a SPA reintroduziria na tela.
    window.location.assign("/");
  };

  return (
    <div className="eop-menu-usuario" ref={caixa}>
      <button
        ref={gatilho}
        type="button"
        className="eop-usuario"
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-controls={aberto ? id : undefined}
        onClick={() => setAberto((v) => !v)}
        title={recolhido ? (eu.nome ?? t("app.conta")) : undefined}
      >
        <span aria-hidden className="eop-avatar">{iniciaisDe(eu.nome)}</span>
        {!recolhido && (
          <>
            <span className="eop-usuario-texto">
              <span className="eop-usuario-nome">{eu.nome ?? t("estado.semRegistro")}</span>
              {/* O PAPEL, não a empresa: a empresa já está no seletor do topo,
                  e repetir o mesmo texto em dois lugares da mesma barra faz o
                  usuário procurar qual dos dois é o que troca. */}
              <span className="eop-usuario-empresa">{eu.papel ?? t("conta.semPapel")}</span>
            </span>
            <ChevronsUpDown aria-hidden size={14} className="eop-usuario-seta" />
          </>
        )}
      </button>

      {aberto && (
        <div className="eop-suspenso" role="menu" id={id} aria-label={t("app.conta")}>
          <div className="eop-suspenso-cabeca">
            <span aria-hidden className="eop-avatar eop-avatar-g">{iniciaisDe(eu.nome)}</span>
            <span className="min-w-0">
              <span className="eop-suspenso-nome">{eu.nome ?? t("estado.semRegistro")}</span>
              <span className="eop-suspenso-sub">
                {/* Só os 4 últimos dígitos. O CPF inteiro na tela é dado
                    pessoal exposto a quem passa atrás da cadeira. */}
                {eu.cpfFinal ? `CPF final ${eu.cpfFinal}` : t("estado.semRegistro")}
                {eu.papel ? ` · ${eu.papel}` : ""}
              </span>
            </span>
          </div>

          <a className="eop-suspenso-item" role="menuitem" href="/conta">
            <User aria-hidden size={15} /><span>{t("conta.perfil")}</span>
          </a>
          <a className="eop-suspenso-item" role="menuitem" href="/conta/senha">
            <KeyRound aria-hidden size={15} /><span>{t("conta.senha")}</span>
          </a>

          <button
            type="button" role="menuitem" className="eop-suspenso-item"
            onClick={() => setTema(alternarTema())}
          >
            {tema === "claro"
              ? <Moon aria-hidden size={15} />
              : <Sun aria-hidden size={15} />}
            <span>{tema === "claro" ? t("tema.escuro") : t("tema.claro")}</span>
          </button>

          <a className="eop-suspenso-item" role="menuitem" href="/ajuda">
            <LifeBuoy aria-hidden size={15} /><span>{t("conta.ajuda")}</span>
          </a>

          <div className="eop-suspenso-risco" role="separator" />

          <button
            type="button" role="menuitem"
            className="eop-suspenso-item eop-suspenso-sair"
            onClick={() => void sair()} disabled={saindo}
          >
            <LogOut aria-hidden size={15} />
            <span>{saindo ? t("conta.saindo") : t("conta.sair")}</span>
          </button>
        </div>
      )}
    </div>
  );
}
